import React, { useState, useEffect } from 'react';
import { Form, InputField, RegularTextAreaField } from 'components/Form';
import { Button, Dialog, DialogTitle, Spinner } from 'components/Elements';

import { searchCustomers } from 'features/users';
import { X } from 'react-feather';
import { getCustomerWallets } from '../api/getCustomerWallets';
import { WalletCard } from './WalletCard';
import Lottie from 'lottie-react';
import checkAnimation from 'assets/lottie/check.json';
import { useWithdrawFromWallet } from '../api/withdrawToWallet';
import WithdrawSvg from 'assets/withdraw.svg';

export const WithdrawToCustomer = ({
  depositPage,
  customerId: defaultId,
  customerName: defaultName,
}) => {
  const [show, setShow] = useState(false);
  const [customerId, setCustomerId] = useState(defaultId);
  const [isLoading, setIsLoading] = useState();
  const [searchResults, setSearchResults] = useState(null);
  const [searchText, setSearchText] = useState();
  const [showSuccessMessage, setShowSuccessMessage] = useState();
  const [walletResults, setWalletResults] = useState();
  const [walletError, setWalletError] = useState();
  const [isFetchingWallets, setIsFetchingWallets] = useState();
  const [customerName, setCustomerName] = useState();
  const [clientWalletId, setClientWalletId] = useState();
  const [currencyId, setCurrencyId] = useState();
  const [currencyCode, setCurrencyCode] = useState();

  const withdrawFromWalletMutation = useWithdrawFromWallet();

  useEffect(() => {
    if (customerId) {
      setIsFetchingWallets(true);
      getCustomerWallets({ customerId }).then((result) => {
        setWalletResults(result?.walletDetails);
        setIsFetchingWallets(false);
      });
      setCustomerName(defaultName);
    }
  }, [customerId, defaultName]);

  useEffect(() => {
    if (show) {
      setCustomerId(defaultId);
      setCustomerName(defaultName);
    }
  }, [show, defaultId, defaultName]);

  const handleClose = () => {
    setShow(false);
    setSearchResults(null);
    setSearchText(null);
    setWalletResults(null);
    setCustomerId(null);
    setCustomerName(null);
    setClientWalletId(null);
    setCurrencyCode(null);
    setCurrencyId(null);
    setShowSuccessMessage(null);
  };
  const handleOpen = () => setShow(true);

  const handleSearchSubmit = async (e) => {
    e.preventDefault();

    setWalletError(null);
    setIsLoading(true);
    setSearchResults(null);

    if (searchText.trim()) {
      try {
        const response = await searchCustomers({
          text: searchText,
          customerId: 0,
        });
        setIsLoading(false);
        console.log(response);
        if (response?.users?.length > 0) {
          setSearchResults(response);
        }
      } catch (error) {
        console.error('Error searching for admin:', error);
      }
    }
  };

  return (
    <div>
      <Button
        variant="glass"
        size="sm"
        startIcon={
          <div className="">
            <img src={WithdrawSvg} alt="deposit" className="w-5 h-5 mr-2" />
          </div>
        }
        onClick={handleOpen}
      >
        <span>Deduct from wallet</span>
      </Button>

      <Dialog isOpen={show} onClose={handleClose} initialFocus={null}>
        <div className="bg-[linear-gradient(to_bottom,_#2F6ACB,_#4455D0,_#4B3CBF)] dark:bg-[linear-gradient(to_bottom,_#0F2027,_#203A43,_#2C5364)] shadow-2xl rounded-2xl p-6  w-full text-white inline-block align-bottom px-4 pt-5 pb-4 text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          <div className="flex flex-row justify-between items-center mb-3">
            <DialogTitle className="text-xl text-start text-white font-bold">
              {showSuccessMessage === true ? '' : 'Deduct from wallet'}
            </DialogTitle>
            <button onClick={handleClose}>
              <X />
            </button>
          </div>
          {isFetchingWallets ? (
            <div className="flex flex-row items-center space-x-2 justify-center">
              <Spinner size="md" />
            </div>
          ) : (
            <>
              {showSuccessMessage ? (
                <>
                  {' '}
                  <div className="flex flex-col items-center">
                    <Lottie
                      animationData={checkAnimation}
                      className="w-48"
                      loop
                    />
                    <div className="mt-4 text-center">
                      <h2 className="text-2xl font-bold text-green-400">
                        Withdraw Successful!
                      </h2>
                      <p className="mt-2 text-white">
                        You’ve just withdrawn from{' '}
                        <span className="capitalize font-bold text-green-600 dark:text-green-400">
                          {customerName}'s
                        </span>{' '}
                        wallet.
                      </p>
                      <p className="mt-1 text-white">
                        Their balance has been updated. <br />
                        Your withdraw was successful.
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {customerId ? (
                    <>
                      {walletResults ? (
                        <>
                          <div className="text-md font-bold capitalize mb-3">
                            List of {customerName}'s Wallets.
                          </div>
                          {walletResults.map((wallet) => (
                            <button
                              type="button"
                              className="w-full"
                              onClick={() => {
                                setClientWalletId(wallet.id);
                                setCurrencyId(wallet.currencyId);
                                setCurrencyCode(wallet.currencyCode);
                                setWalletResults(null);
                              }}
                            >
                              <WalletCard key={wallet.id} wallet={wallet} />
                            </button>
                          ))}
                        </>
                      ) : (
                        <>
                          <Form
                            id="withdraw-to-wallet"
                            onSubmit={async (values) => {
                              const data = {
                                clientWalletId,
                                clientId: customerId,
                                currencyId,
                                currencyCode,
                                ...values,
                              };
                              const result =
                                await withdrawFromWalletMutation.mutateAsync({
                                  data,
                                });
                              if (result) {
                                console.log(result);
                                setShowSuccessMessage(true);
                              }
                            }}
                          >
                            {({ register, formState }) => (
                              <>
                                <div className="text-md font-bold capitalize">
                                  Provide amount to deduct
                                </div>
                                <InputField
                                  label="Amount"
                                  error={formState.errors['amount']}
                                  registration={register('amount')}
                                />

                                <RegularTextAreaField
                                  label="Reason"
                                  error={formState.errors['reason']}
                                  registration={register('reason')}
                                />

                                <div className="mt-2 flex flex-row items-center space-x-2">
                                  <Button
                                    className="w-full"
                                    variant="glass"
                                    isLoading={
                                      withdrawFromWalletMutation.isLoading
                                    }
                                    type="submit"
                                  >
                                    Continue
                                  </Button>
                                </div>
                              </>
                            )}
                          </Form>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="text-lg font-semibold">
                        Search for customer
                      </div>
                      <div className="text-md mb-3">
                        Search and select a customer to preview the wallets
                      </div>
                      <form
                        onSubmit={handleSearchSubmit}
                        className="flex justify-center items-center"
                      >
                        <input
                          type="text"
                          placeholder="Search for customer's name..."
                          className="text-sm mx-auto w-full px-4 shadow dark:shadow-none py-4 rounded-full focus:outline-none border border-gray-100 dark:border-gray-800 dark:bg-gray-700 text-gray-800 dark:text-white"
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                        />
                      </form>
                      <div className="py-4">
                        {searchResults ? (
                          <>
                            {isLoading ? (
                              <div className="flex flex-row items-center space-x-2 justify-center">
                                <Spinner size="sm" />{' '}
                                <div className="text-sm">Searching..</div>
                              </div>
                            ) : (
                              <>
                                {searchResults.users.map((user) => (
                                  <div className="mb-4">
                                    <button
                                      onClick={async () => {
                                        setIsLoading(true);
                                        const result = await getCustomerWallets(
                                          {
                                            customerId: user.id,
                                          }
                                        );
                                        console.log('result', result);
                                        if (
                                          result?.walletDetails?.length <= 0
                                        ) {
                                          setIsLoading(false);
                                          setWalletError(
                                            'This user has no wallets.'
                                          );
                                        } else {
                                          setCustomerId(user.id);
                                          setCustomerName(
                                            `${user.firstName} ${user.lastName}`
                                          );
                                          setIsLoading(false);
                                          setWalletResults(
                                            result?.walletDetails
                                          );
                                          console.log(walletResults);
                                        }
                                      }}
                                      type="button"
                                      key={user.id}
                                      className={`p-1 flex items-center border ${
                                        walletError
                                          ? 'border-red-400'
                                          : 'border-gray-100 dark:border-gray-700'
                                      }   hover:bg-gray-100 dark:hover:bg-gray-700 rounded-3xl w-full`}
                                    >
                                      <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center overflow-hidden">
                                        {user.photo ? (
                                          <img
                                            src={user.photo}
                                            alt={`${user.firstName} ${user.lastName}`}
                                            className="w-full h-full object-cover"
                                          />
                                        ) : (
                                          <span className="text-gray-500 ">
                                            No Photo
                                          </span>
                                        )}
                                      </div>
                                      <div className="ml-4">
                                        <p className="text-lg text-start font-medium">
                                          {user.firstName} {user.lastName}
                                        </p>
                                        <p className="text-gray-600 dark:text-gray-400">
                                          {user.email || 'No email provided'}
                                        </p>
                                        {user.phoneNumber && (
                                          <p className="text-gray-600 dark:text-gray-400">
                                            {user.phoneNumber}
                                          </p>
                                        )}
                                      </div>
                                    </button>
                                    <div className="text-red-500 mt-2 text-center">
                                      {walletError ? walletError : null}
                                    </div>
                                  </div>
                                ))}
                              </>
                            )}
                          </>
                        ) : (
                          <></>
                        )}
                      </div>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </Dialog>
    </div>
  );
};
